<template>
  <div class="heatmap-widget">
    <PrioChart
      v-if="!isLoading && highestValue > 0 && heatmapData.heatmap_matrix?.flat().some(v => v > 0)"
      :option="heatmapOption"
      :active="true"
      :height="340"
      name="HeatmapDashboard"
    />
    <p v-else class="no-data-message" style="padding-top: 100px;">
      {{ $t('charts.heatmap.noEntranceData') }}
    </p>
    <div v-if="isLoading" class="loading-overlay">
      <div class="loader"></div>
    </div>
    <div v-else-if="highestValue === 0" class="heatmap-overlay">
      <div>
        <div v-if="isLoading" class="loading-overlay">
          <div class="loader"></div>
        </div>  
        <p v-else class="no-data-message">
          {{ $t('charts.heatmap.noEntranceData') }}
        </p>
      </div>
    </div>
  </div>
</template>
  
<script>
import PrioChart from '@/components/ui/PrioChart.vue'

import { ref, watch, computed } from 'vue';
import { use } from 'echarts/core';
import { HeatmapChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  TitleComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { currentLanguage } from '@/i18n'
import { formatNumber, formatWeekday } from '@/i18n/format'

use([
   HeatmapChart,
   GridComponent,
   TooltipComponent,
   VisualMapComponent,
   TitleComponent,
   CanvasRenderer
])

export default {
  name: 'HeatmapDashboard',

  components: {
    PrioChart
  },
  
  props: {
    heatmapData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    selectedDuration: {
      type: Number,
      required: true,
    }
  },
    
  setup(props) {
    const highestValue = ref(0)
    const isLoading = ref(true)
    const screenWidth = ref(window.innerWidth)

    // Dutch day names come from format.js: "ma" … / "maandag" … (1 Jan 2024 was a Monday).
    const currentDayLabels = computed(() =>
      currentLanguage() !== 'en'
        ? Array.from({ length: 7 }, (_, i) => formatWeekday(new Date(2024, 0, 1 + i), { short: screenWidth.value < 800 }))
        : screenWidth.value < 800
        ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] // Abbreviated labels
        : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] // Full labels
    )

    const hourLabels = computed(() => {
      const { minHour, maxHour } = props.heatmapData
      const labs = []
      for (let h = minHour; h <= maxHour; h++) {
        labs.push(`${h.toString().padStart(2,'0')}:00`)
      }
      return labs
    })
  
    const formatHeatmapData = () => {
       if (
         !props.heatmapData.heatmap_matrix ||
         !Array.isArray(props.heatmapData.heatmap_matrix)
       ) {
         return []
       }
       const matrix = props.heatmapData.heatmap_matrix
       const data = []
       matrix.forEach((dayValues, dayIdx) => {
         dayValues.forEach((val, hrIdx) => {
           data.push([ hrIdx, dayIdx, val || 0 ])
         })
       })
       return data
     }
  
    const heatmapOption = computed(() => ({
       // Dutch numbers read 1.234; English keeps ECharts' own formatting.
       tooltip: currentLanguage() === 'en' ? { position: 'top' } : { position: 'top', valueFormatter: v => formatNumber(Array.isArray(v) ? v[2] : v) },
       grid:    { top: '6%', left: '13%', height: '62%', width: '80%'},
       xAxis: {
         type: 'category',
         data: hourLabels.value,
         splitArea: { show: true }
       },
       yAxis: {
         type: 'category',
         data: currentDayLabels.value,
         splitArea: { show: true }
       },
       visualMap: {
         min: 0,
         max: highestValue.value || 10,
         calculable: true,
         orient: 'horizontal',
         left: 'center',
         bottom: '6%',
         textStyle: { color: 'grey'},
         ...(currentLanguage() === 'en' ? {} : { formatter: v => formatNumber(v) }),
         inRange: {
           color: [ 
              'rgba(241,249,232,0.2)',
              'rgba(206,235,188,0.4)',
              'rgba(152,204,124,0.6)',
              'rgba(54,145,13,0.8)'
           ]
         }
       },
       series: [{
         type: 'heatmap',
         data: formatHeatmapData(),
         emphasis: {
           itemStyle: {
             shadowBlur: 10,
             shadowColor: 'rgba(0,0,0,0.5)'
           }
         }
       }]
     }))

    watch( () => props.selectedDuration, (newData) => {
      if (newData) {
        isLoading.value = true
      }
    })

    watch(
       () => props.heatmapData.heatmap_matrix,
       (newMatrix) => {
         if (Array.isArray(newMatrix) && newMatrix.length) {
           highestValue.value = Math.max(...newMatrix.flat())
         } else {
           highestValue.value = 0
         }
         isLoading.value = false
       },
       { immediate: true, deep: true }
     )

    return {
      heatmapOption,
      isLoading,
      highestValue
    }
  },
}

</script>

<style scoped>
.heatmap-widget {
  position: relative;
  width: 100%;
  height: 100%;
}

.heatmap-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  padding-top: 10px;
  align-items: center;
  background-color: rgba(255, 255, 255);
  z-index: 10;
}

.no-data-message {
    color: gray;
    font-size: 14px; /* Adjust font size to match the design */
    font-style: italic; /* Optional for emphasizing no data */
    text-align: center; /* Center the message */
    margin-top: 10px; /* Add some spacing from the top */
    margin-bottom: 20px; /* Add some spacing from the bottom */
    line-height: 1.5; /* Improve readability with line height */
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.loader {
  width: 20px;
  padding: 2px;
  margin-top: 10px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #B2C149;
  --_m: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m);
  mask: var(--_m);
  -webkit-mask-composite: source-out;
  mask-composite: subtract;
  animation: spinner 1s infinite linear;
}

@keyframes spinner {
  to {
    transform: rotate(1turn);
  }
}
</style>